import { useConfig } from '../api'
import { useEffect, useState } from 'react'
import { replaceVariables } from '@sesamiapp/translation'
import * as Sentry from '@sentry/browser'

export type TranslateKey = null | string | TemplateStringsArray
export type TranslateFunction = (key: TranslateKey, replacements?: Record<string, string | number | null | undefined>) => string

type Translations = Record<string, string>

// the parameter keeps the import dynamic, so webpack resolves it as a context
// import like the locale imports below, instead of a static import that
// CRA's ModuleScopePlugin rejects for being outside src/:
const importTranslations = (locale: string) => import(`../../translations/${locale}.json`)

export const useTranslate = () => {

    const { data: config, isLoading } = useConfig()
    const [ translations, setTranslations ] = useState<Translations>({})
    const [ fallbackTranslations, setFallbackTranslations ] = useState<Translations>({})

    useEffect(() => {
        if(config){
            importTranslations(config.locale).then(module => {
                setTranslations(module.default)
            }).catch(() => { // if there is no file for the locale, fall back to en-US:
                importTranslations('en-US').then(module => {
                    setTranslations(module.default)
                })
            })
            if(config.locale !== 'en-US'){ // en-US phrases as a fallback for missing keys:
                importTranslations('en-US').then(module => {
                    setFallbackTranslations(module.default)
                })
            }
        }
    }, [config])

    return isLoading ? {
        __: () => '',
        isLoading: true,
        isError: false
    } : !config ? {
        __: () => '',
        isLoading: false,
        isError: true
    } : {
        __: (key: TranslateKey, replacements?: Record<string, string | number | null | undefined>) => {

            if(key && Object.keys(translations).length){ // if the file is loaded:
                const dictionaryKey = key.toString()
                const translation = translations[dictionaryKey] ?? fallbackTranslations[dictionaryKey]
                if(translation){ // if translation found, or an en-US fallback exists:
                    return replaceVariables(translation, replacements)
                }else{
                    console.error('[Sesami] Phrase not found:', config.locale, key)
                    Sentry.captureException('Phrase not found', {
                        extra: { locale: config.locale, key }
                    })
                    return dictionaryKey
                }
            }

            // if the file is not loaded yet:
            return ''

        },
        isLoading: false,
        isError: false
    }
}

export const generatePhraseFromKey = (key: TranslateKey) => {
    let phrase = ''
    if(key){
        const keyString = key.toString()
        phrase = keyString.toLowerCase().replaceAll('_', ' ')
        phrase = phrase.replace(phrase[0], phrase[0].toUpperCase())
    }
    return phrase
}
